import styles from '../../style.module.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { isEqual, includes } from 'lodash';
import {
    Layout,
    FormFieldInputNew,
    FormFieldSelect,
    Button,
    Card,
    useForm,
    extractErrorMessage,
    ButtonsGroup,
    Text,
    FormFieldUploadFile
} from '@netapp/bxp-design-system-react';
import { history } from 'store';
import { apiClient } from 'utils/clients';
import { notificationSettings as i18nNamespace } from '../../../../i18n/namespace';
import PermissionComponent from "modules/app/components/permission/PermissionComponent";
import TestEmailConfigDialog from './TestEmailConfigDialog';
import { ConfigurationBaseContent, ConfigurationBaseFooter, ConfigurationBaseHeader, ConfigurationBaseLayout } from 'modules/console/configurations';
import { ReactComponent as EmailConfigureIcon } from './../../assets/icon-email-configure.svg';
import { getUploadedFileSizeBytes, SMTP_CERT_MAX_FILE_SIZE_BYTES, SMTP_CERT_MAX_FILE_SIZE_KB } from 'utils/notificationsUtils';

const SECURITY_CONFIGURATION_OPTIONS = {
    STARTTLS: 'starttls',
    SMTPS: 'smtps'
};

export const EmailServerConfigForm = ({
    form,
    t,
    connectionSecurityOptions,
    onConnectionSecurityChange,
    isFirstTimeRegister,
    rules,
    isEmailConfigSaveInProgress,
    handleTestEmailConfig,
    isSMTPOnboardingFlow
}) => {
    return (
        <>
            <Layout.Grid rowGap='md'>
                <Layout.GridItem lg={6} md={6}>
                    <FormFieldInputNew
                        form={form}
                        label={t('sender_name')}
                        name='fromName'
                        showError={(form.dirty.fromName || form.submissionAttempted) && form.formErrors.fromName}
                    />
                </Layout.GridItem>
                <Layout.GridItem lg={6} md={6}>
                    <FormFieldInputNew
                        form={form}
                        type='email'
                        label={t('sender_email')}
                        name='fromAddress'
                        showError={(form.dirty.fromAddress || form.submissionAttempted) && form.formErrors.fromAddress}
                    />
                </Layout.GridItem>
                <Layout.GridItem {...(isSMTPOnboardingFlow ? { lg: 8, md: 8 } : { lg: 6, md: 6 })}>
                    <FormFieldInputNew
                        form={form}
                        label={t('smtp_hostname')}
                        name='host'
                        showError={(form.dirty.host || form.submissionAttempted) && form.formErrors.host}
                    />
                </Layout.GridItem>
                <Layout.GridItem {...(isSMTPOnboardingFlow ? { lg: 4, md: 4 } : { lg: 6, md: 6 })}>
                    <FormFieldInputNew
                        type='number'
                        form={form}
                        label={t('port')}
                        name='port'
                        showError={(form.dirty.port || form.submissionAttempted) && form.formErrors.port}
                    />
                </Layout.GridItem>
                <Layout.GridItem {...(isSMTPOnboardingFlow ? { lg: 4, md: 4 } : { lg: 6, md: 6 })}>
                    <FormFieldSelect
                        form={form}
                        label={t('connection_security')}
                        name='connectionSecurity'
                        options={connectionSecurityOptions}
                        onChange={onConnectionSecurityChange}
                        showError={(form.dirty.connectionSecurity || form.submissionAttempted) && form.formErrors.connectionSecurity}
                    />
                </Layout.GridItem>
                <Layout.GridItem {...(isSMTPOnboardingFlow ? { lg: 8, md: 8 } : { lg: 6, md: 6 })}>
                    <FormFieldUploadFile
                        isOptional={true}
                        form={form}
                        label={t('self_signed_certificate')}
                        name="tlsCertificate"
                        placeholder={t('upload_certificate')}
                        showError={(form.dirty.tlsCertificate || form.submissionAttempted) && form.formErrors.tlsCertificate}
                        errorMessage={form.formErrors.tlsCertificate}
                    />
                </Layout.GridItem>
                <Layout.GridItem lg={6} md={6}>
                    <FormFieldInputNew
                        isOptional={true}
                        form={form}
                        label={t('username')}
                        name='username'
                        showError={(form.dirty.username || form.submissionAttempted) && form.formErrors.username}
                    />
                </Layout.GridItem>
                <Layout.GridItem lg={6} md={6}>
                    <FormFieldInputNew
                        isOptional={true}
                        form={form}
                        type='password'
                        label={t('password')}
                        name='password'
                        showError={(form.dirty.password || form.submissionAttempted) && form.formErrors.password}
                    />
                </Layout.GridItem>
            </Layout.Grid>
            <div className={styles.marginTop16}>
                <Layout.Grid rowGap='md'>
                    <Layout.GridItem lg={12} className={styles.flexJustifyStart}>
                        <PermissionComponent hasPermssion={isSMTPOnboardingFlow ? true : (!isFirstTimeRegister ? includes(rules, 'bxp:notifications-modify') : includes(rules, 'bxp:notifications-create'))}>
                            <Button color='secondary' disabled={isEmailConfigSaveInProgress || !form.isValid } onClick={form.handleFormSubmit(handleTestEmailConfig)} className={styles.width152}>
                                {t('test_email')}
                            </Button>
                        </PermissionComponent>
                    </Layout.GridItem>
                </Layout.Grid>
            </div>
        </>
    );
};

const EmailServerConfiguration = React.memo(({ onClose }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { t } = useTranslation(i18nNamespace);
    const { selectedOrgId, selectedOrg } = useSelector(state => state.tenancy);
    const [isTestEmailConfigOpen, setIsTestEmailConfigOpen] = useState(false);
    const [isTestEmailSuccess, setIsTestEmailSuccess] = useState(false);
    const [isFirstTimeRegister, setIsFirstTimeRegister] = useState(true);
    const [isEmailConfigSaveInProgress, setIsEmailConfigSaveInProgress] = useState(false);
    const [isFetchEmailConfigInProgress, setIsFetchEmailConfigInProgress] = useState(true);
    const [webhookDetailsFetchError, setWebhookDetailsFetchError] = useState(false);
    const vendorizedProductName = useSelector(state => state.app.vendorizedProductName);

    const connectionSecurityOptions = [
        { label: t('STARTTLS'), value: SECURITY_CONFIGURATION_OPTIONS.STARTTLS },
        { label: t('SMTPS'), value: SECURITY_CONFIGURATION_OPTIONS.SMTPS }
    ];

    const initialEmailServerConfigState = {
        id: '',
        host: '',
        connectionSecurity: connectionSecurityOptions[0],
        port: 25,
        username: '',
        password: '',
        fromName: '',
        fromAddress: '',
        startTLS: true,
        ssl: false,
        tlsCertificate: null
    };

    const validateEmailConfigSchema = yup.object().shape({
        host: yup
            .string()
            .required(t('smtp_hostname_required')),
        port: yup
            .number()
            .min(1, t('smtp_port_must_be_greater_than_zero'))
            .max(65535, t('smtp_port_must_be_less_than_65536'))
            .typeError(t('smtp_port_must_be_number'))
            .required(t('smtp_port_required')),
        username: yup
            .string(),
        password: yup
            .string(),
        fromName: yup
            .string()
            .required(t('smtp_from_name_required')),
        fromAddress: yup
            .string()
            .email(t('smtp_from_address_invalid'))
            .required(t('smtp_from_address_required')),
        tlsCertificate: yup
            .mixed()
            .nullable()
            .test(
                'smtp-certificate-max-size',
                t('certificate_max_size', {
                    maxSize: `${SMTP_CERT_MAX_FILE_SIZE_KB} KB`,
                }),
                value => {
                    if (!value) {
                        return true;
                    }
                    return getUploadedFileSizeBytes(value) <= SMTP_CERT_MAX_FILE_SIZE_BYTES;
                }
            )
    }).test('username-password', null, function (values) {
        const { username, password } = values;
        const hasUsername = !!username;
        const hasPassword = !!password;
        if (hasUsername && !hasPassword) {
            return this.createError({ path: 'password', message: t('smtp_password_required') });
        }
        if (!hasUsername && hasPassword) {
            return this.createError({ path: 'username', message: t('smtp_username_required') });
        }
        return true;
    });


    const getKeycloakResp = (payload) => {
        return {
            host: payload.host,
            port: `${payload.port}`,
            from: payload.fromAddress,
            fromDisplayName: payload.fromName,
            ssl: `${payload.ssl}`,
            starttls: `${payload.startTLS}`,
            authentication: (payload.username && payload.password) ? 'true' : 'false',
            ...((payload.username && payload.username !== '') && { authenticationUser: payload.username }),
            ...((payload.password && payload.password !== '') && { authenticationPassword: payload.password })
        }
    }

    const form = useForm(initialEmailServerConfigState, validateEmailConfigSchema);

    const checkIsSaveDisabled = () => {
        if (isFirstTimeRegister && !form.isValid) return true;
        if (!isFirstTimeRegister && isEqual(form.formState, initialEmailServerConfigState) && !form.isValid) return false;
        if (!isFirstTimeRegister && !(!isEqual(form.formState, initialEmailServerConfigState) && form.isValid)) return true;
        return false;
    };
    const [isSaveDisabled, setIsSaveDisabled] = useState(checkIsSaveDisabled());

    const onConnectionSecurityChange = ({value}) => {
        if (value === 'starttls') {
            form.handleFormChange(connectionSecurityOptions[0], { name: 'connectionSecurity' });
            form.handleFormChange(25, { name: 'port' });
            form.handleFormChange(true, { name: 'startTLS' });
            form.handleFormChange(false, { name: 'ssl' });
        } else if (value === 'smtps') {
            form.handleFormChange(connectionSecurityOptions[1], { name: 'connectionSecurity' });
            form.handleFormChange(465, { name: 'port' });
            form.handleFormChange(false, { name: 'startTLS' });
            form.handleFormChange(true, { name: 'ssl' });
        }
    }

    useEffect(() => {
        const fetchEmailConfig = async () => {
            try {
                const response = await apiClient.get(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config`);
                if (response.data) {
                    const emailConfig = response.data;
                    form.handleFormChange(emailConfig.host, { name: 'host' });
                    form.handleFormChange(emailConfig.username, { name: 'username' });
                    form.handleFormChange(emailConfig.fromName, { name: 'fromName' });
                    form.handleFormChange(emailConfig.fromAddress, { name: 'fromAddress' });
                    form.handleFormChange(emailConfig.id, { name: 'id' });
                    form.handleFormChange(emailConfig.port, { name: 'port' });
                    if (emailConfig.startTLS) {
                        form.handleFormChange(connectionSecurityOptions[0], { name: 'connectionSecurity' });
                        form.handleFormChange(true, { name: 'startTLS' });
                        form.handleFormChange(false, { name: 'ssl' });
                    } else if (emailConfig.ssl) {
                        form.handleFormChange(connectionSecurityOptions[1], { name: 'connectionSecurity' });
                        form.handleFormChange(false, { name: 'startTLS' });
                        form.handleFormChange(true, { name: 'ssl' });
                    }
                    setIsFirstTimeRegister(false);
                    setIsSaveDisabled(true);
                }
            } catch (error) {
                setWebhookDetailsFetchError(true);
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: t('failed_to_fetch_email_config', i18nNamespace),
                        exception: error,
                        moreInfo: extractErrorMessage(error, 'detail'),
                        key: 'GET-EMAIL-CONFIG',
                        showGlobal: true
                    }
                });
                setIsFirstTimeRegister(true);
            } finally {
                setIsFetchEmailConfigInProgress(false);
            }
        };
        setIsFetchEmailConfigInProgress(true);
        fetchEmailConfig();
    }, []);

    useEffect(() => {
        setIsSaveDisabled(checkIsSaveDisabled());
    }, [form.formState]);

    const handleSaveEmailConfig = async () => {
        setIsEmailConfigSaveInProgress(true);
        if (!form.isValid) {
            return;
        }
        const payload = { ...form.formState };
        payload.tlsCertificate = payload.tlsCertificate ? payload.tlsCertificate.contents : '';
        payload.port = Number(payload.port);
        delete payload.connectionSecurity;
        delete payload.id;
        if (payload.username === '' || payload.password === '') {
            delete payload.username;
            delete payload.password;
        }
        try {
            const response = await (isFirstTimeRegister ?
                apiClient.post(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config`, payload) :
                apiClient.patch(`${process.env.REACT_APP_OCCM_ENDPOINT}organizations/${selectedOrgId}/email-config/${form?.formState?.id}`, payload));
            if (response.data) {
                try {
                    await apiClient.post(`${process.env.REACT_APP_OCCM_ENDPOINT}api/v1/smtp`, getKeycloakResp(payload));
                } catch (error) {
                    console.error('Error in saving email config to keycloak', extractErrorMessage(error));
                    console.error('Error in saving email config to keycloak', error);
                }
                dispatch({
                    type: 'NOTIFICATIONS:SUCCESS',
                    payload: {
                        message: isFirstTimeRegister ? t('email_config_added_successfully', i18nNamespace) : t('email_config_modified_successfully', i18nNamespace),
                        key: isFirstTimeRegister ? 'POST-EMAIL-CONFIG' : 'PATCH-EMAIL-CONFIG',
                        showGlobal: true
                    }
                });
                form.resetForm(initialEmailServerConfigState, true);
                navigateToConfigurations();
            }
        } catch (error) {
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: isFirstTimeRegister ? t('failed_to_post_email_config', i18nNamespace) : t('failed_to_patch_email_config', i18nNamespace),
                    exception: error,
                    moreInfo: extractErrorMessage(error, 'detail'),
                    key: 'POST-EMAIL-CONFIG',
                    showGlobal: true
                }
            });
        } finally {
            setIsEmailConfigSaveInProgress(false);
        }
    };

    const navigateToConfigurations = () => {
        if (onClose) onClose();
        else if (location.state?.fromConsoleAssistant) history.push('/home', { openConsoleAssistant: true });
        else history.push(`/console-configurations`);
    }

    const handleTestEmailConfig = async () => {
        if (!form.isValid) {
            return;
        }
        setIsTestEmailConfigOpen(true);
    };

    const onSaveClick = (event) => {
        form.handleFormSubmit(handleSaveEmailConfig)(event);
    }

    return <>
        <ConfigurationBaseLayout isLoading={isFetchEmailConfigInProgress}>
            <ConfigurationBaseHeader label={!isFirstTimeRegister ? t('edit_email_server') : t('configure_email_server')} onClose={navigateToConfigurations} />
            {webhookDetailsFetchError && (<Layout.Page>
                <Layout.Content>
                    <Layout.Container>
                        <Text>{t('failed_to_load_email_details')}</Text>
                    </Layout.Container>
                </Layout.Content>
            </Layout.Page>)}
            {!webhookDetailsFetchError && (<ConfigurationBaseContent
                description={t('configure_email_notifications', { appName: vendorizedProductName })}
                iconRenderer={() => <EmailConfigureIcon size='24px' />}
                status={isFirstTimeRegister ? 'Not configured' : 'Configured'}
            >
                {<Card className={styles.cardPadding32}>
                        <EmailServerConfigForm
                            form={form}
                            t={t}
                            connectionSecurityOptions={connectionSecurityOptions}
                            onConnectionSecurityChange={onConnectionSecurityChange}
                            isFirstTimeRegister={isFirstTimeRegister}
                            rules={selectedOrg?.rules}
                            isEmailConfigSaveInProgress={isEmailConfigSaveInProgress}
                            handleTestEmailConfig={handleTestEmailConfig}
                            isSMTPOnboardingFlow={false}
                        />
                    </Card>
                }
                {isTestEmailConfigOpen && <TestEmailConfigDialog emailServerConfigDetailsForm={form} setIsTestEmailConfigOpen={setIsTestEmailConfigOpen} onSuccess={() => setIsTestEmailSuccess(true)} />}
            </ConfigurationBaseContent>)}
            <ConfigurationBaseFooter>
                <ButtonsGroup>
                    <PermissionComponent hasPermssion={!isFirstTimeRegister ? includes(selectedOrg?.rules, 'bxp:notifications-modify') : includes(selectedOrg?.rules, 'bxp:notifications-create')}>
                        <Button 
                            className={styles.width152}
                            isSubmitting={isEmailConfigSaveInProgress}
                            disabled={isSaveDisabled || !isTestEmailSuccess}
                            onClick={onSaveClick}>
                                {t('save')}
                        </Button>
                    </PermissionComponent>
                    <Button
                        color="secondary"
                        isDisabled={isEmailConfigSaveInProgress}
                        className={styles.width152MarginLeft8}
                        onClick={navigateToConfigurations}>
                        {t('cancel')}
                    </Button>
                </ButtonsGroup>
            </ConfigurationBaseFooter>
        </ConfigurationBaseLayout>
    </>;
});

export default EmailServerConfiguration;